import React, { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Box, Fab } from '@mui/material';
import { ShoppingCart } from '@mui/icons-material';
import { toastWarning } from 'utils/toast';
import { DropsContext } from 'contexts/DropsContext';
import { RequisicoesContext } from 'contexts/RequisicoesContext';
import { GridContext } from 'contexts/GridContext';
import { AuthContext } from 'contexts/AuthContext';
import { useModal, useDialog } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import CompraModal from './Modals/CompraModal';
import CotacaoModal from './Modals/CotacaoModal';
import CarrinhoModal from './Modals/CarrinhoModal';
import getRequisicoesFilterFields from './filterFields';
import getRequisicoesColumnOverrides from './columnOverrides';

const Requisicoes = () => {
  const rotina = 'RequisicoesCompra';
  const titulo = 'Requisições de Compra';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal } = useModal();
  const { openDialog, closeDialog } = useDialog();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { requisicoes, getRequisicoes, getLoading, pedidos, setPedidos } =
    useContext(RequisicoesContext);
  const filterFields = useMemo(
    () => getRequisicoesFilterFields(drops, user),
    [drops, user]
  );
  const columnOverrides = useMemo(
    () => getRequisicoesColumnOverrides(openDialog),
    [openDialog]
  );

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const getFilterValues = (filter = getValues()) => {
    if (user?.permissoes?.block_compras_requisicoes_grid) {
      return { ...filter, requisitante: [user?.id] };
    }
    return filter;
  };

  const loadGrid = (props = {}) => {
    const filter = getFilterValues(props?.filter);
    getRequisicoes({ ...props, rotina, filter });
  };

  const onRequestCompra = (data) => {
    const finder = pedidos?.some((s) => s?.cadastro_id === data?.cadastro_id);
    if (finder) {
      setPedidos((prev) =>
        prev?.map((p) => {
          if (p?.cadastro_id === data?.cadastro_id) {
            return { ...p, itens: [...(p?.itens || []), data] };
          }
          return p;
        })
      );
    } else {
      setPedidos((prev) => [
        ...prev,
        {
          cadastro_id: data?.cadastro_id,
          filial_id: data?.filial_id,
          cadastro: data?.cadastro,
          itens: [data],
        },
      ]);
    }
  };

  const toolbarActions = [
    {
      name: 'Adicionar Requisição de Compra',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Requisicoes/Gerar'),
      show: !user?.permissoes?.block_compras_requisicoes_adicionar,
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({
          rotina,
          filter: getFilterValues(),
          order: requisicoes?.order,
        }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({
          rotina,
          filter: getFilterValues(),
          order: requisicoes?.order,
        }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={requisicoes?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getFilterValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Gerar Pedido',
      icon: 'post_add',
      action: (rows) => {
        if (!rows?.length) {
          return toastWarning('Secione ao menos um item');
        }
        if (rows?.some((s) => s?.status !== 'PENDENTE')) {
          return toastWarning('Secione somente itens pendentes');
        }
        if (rows?.some((s) => s?.quantidade <= 0)) {
          return toastWarning(
            'Secione somente itens com quantidade disponível'
          );
        }
        const itens = rows?.map((r) => ({
          Origens: [{ documento_item_org_id: r?.id }],
          produto_id: r?.produto_id,
          descricao: r?.descricao,
          quantidade: r?.quantidade,
          unidade_medida_id: r?.unidade_medida_id,
          referencia: r?.referencia,
          desconto1_pc: r?.desconto1_pc,
          desconto_vlr: r?.desconto_vlr || 0,
          preco: r?.preco,
          przent: r?.przent,
          planoconta_id: r?.planoconta_id,
          ccusto_id: r?.ccusto_id,
          aplicacao: r?.aplicacao,
          frota: r?.frota,
          infadc: r?.infadc,
          subtotal:
            (r?.preco || 0) * (r?.quantidade || 0) + (r?.desconto_vlr || 0),
        }));
        const Origens = [
          ...new Set(rows?.map((r) => r?.documento_id)?.filter(Boolean)),
        ]?.map((documento_id) => ({ documento_id }));
        navigate('/app/Pedidos/Gerar', { state: { itens, Origens } });
      },
      show: !user?.permissoes?.block_compras_requisicoes_gerar_pedido,
    },
    {
      name: 'Gerar Cotação',
      icon: 'post_add',
      action: (rows) => {
        if (!rows?.length) {
          return toastWarning('Secione ao menos um item');
        }
        if (rows?.some((s) => s?.status !== 'PENDENTE')) {
          return toastWarning('Secione somente itens pendentes');
        }
        openModal(<CotacaoModal itens={rows} reload={() => loadGrid()} />);
      },
      show: !user?.permissoes?.block_compras_requisicoes_cotar,
    },
    {
      name: 'Calcular Necessidade',
      icon: 'calculate',
      action: () => navigate('/app/Requisicoes/Necessidade'),
      show: !user?.permissoes?.block_compras_requisicoes_necessidade,
    },
  ];

  const rowActions = [
    {
      name: 'Aprovação necessária',
      icon: 'priority_high',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'PENDENTE',
    },
    {
      name: 'Aprovado',
      icon: 'check',
      color: 'primary.main',
      show: ({ row }) => row?.aprovacao === 'SIM',
    },
    {
      name: ' Reprovado',
      icon: 'close',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'NAO',
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.documento_id}`),
    },
    {
      name: 'Comprar',
      icon: 'request_quote',
      show: ({ row }) =>
        !user?.permissoes?.block_compras_requisicoes_gerar_pedido &&
        !pedidos?.some((s) => s?.itens?.some((ss) => ss?.id === row?.id)),
      action: ({ row }) =>
        openModal(<CompraModal item={row} onSubmit={onRequestCompra} />),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} titulo={titulo} colunas={requisicoes?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={requisicoes}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading}
          loadGrid={loadGrid}
          getRowHeight={() => 'auto'}
        />
      </Card>
      {!user?.permissoes?.block_compras_requisicoes_gerar_pedido && (
        <Box sx={{ position: 'fixed', bottom: 100, right: 55 }}>
          <Badge
            badgeContent={pedidos?.length}
            color="secondary"
            max={99}
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Fab
              color="primary"
              onClick={() =>
                openDialog(
                  <CarrinhoModal
                    callback={() => {
                      loadGrid();
                      closeDialog();
                      setPedidos([]);
                    }}
                  />,
                  'Pedidos a Gerar'
                )
              }
              sx={{ zIndex: 1 }}
            >
              <ShoppingCart />
            </Fab>
          </Badge>
        </Box>
      )}
    </Container>
  );
};

export default Requisicoes;
